/** @jsxImportSource @emotion/react */
import React, { memo } from 'react';
import { css } from '@emotion/react';
import type { CoreModalProps } from '@vitvio/design-system';
import { CoreButton, CoreModal, useTheme, Flex } from '@vitvio/design-system';
import { useTranslation } from '@vitvio/locales';
import { useUserContext } from '@vitvio/user-context';
import type { Dayjs } from '@vitvio/helpers';
import { PatientConfirmationInfoCard } from './PatientConfirmationInfoCard';

export interface ConfirmPatientModalProps extends Omit<CoreModalProps, 'headerPath' | 'buttons' | 'onClose'> {
  onConfirm: () => void;
  onCancel: () => void;
  isOpen: boolean;
  data?: {
    locationName?: string;
    serviceName?: string;
    plannedStart?: Dayjs;
    plannedStop?: Dayjs;
    /** Display time (actual/estimated); when same as planned, plan suffix is hidden */
    displayStart?: Dayjs;
    displayStop?: Dayjs;
    procedureName?: string;
    patientMrn?: string;
    surgeonName?: string;
    patientName?: string;
    /** When true, time is highlighted (e.g. when surgery is delayed) */
    hasDelay?: boolean;
  };
}

export const ConfirmPatientModal: React.FC<ConfirmPatientModalProps> = memo(
  ({ isOpen, onConfirm, onCancel, data, ...props }) => {
    const { getSpacing } = useTheme();
    const { t } = useTranslation();
    const { timeFormat } = useUserContext();

    const timeFormatStr = timeFormat === '12hr' ? t('common:format.time') : 'HH:mm';
    const dateFormatStr = t('common:format.date');

    const displayStart = data?.displayStart ?? data?.plannedStart;
    const displayStop = data?.displayStop ?? data?.plannedStop;

    const startTime = displayStart?.format(timeFormatStr) ?? null;
    const endTime = displayStop?.format(timeFormatStr) ?? null;
    const surgeryDate = displayStart?.format(dateFormatStr) ?? null;

    const timeRange = startTime && endTime ? `${startTime} - ${endTime}` : null;

    const plannedStartTime = data?.plannedStart?.format(timeFormatStr) ?? null;
    const plannedEndTime = data?.plannedStop?.format(timeFormatStr) ?? null;
    const showPlanSuffix = timeRange && (plannedStartTime !== startTime || plannedEndTime !== endTime);
    const unavailableText = t('recordingConsole:patientConfirmation.unavailable');

    return (
      <CoreModal
        isOpen={isOpen}
        onClose={onCancel}
        headerPath="recordingConsole:patientConfirmation.header"
        size="xl"
        renderFooter={() => (
          <>
            <CoreButton label="common:button.confirm" onClick={onConfirm} type="primary" size="md" />
            <CoreButton label="common:button.cancel" onClick={onCancel} type="secondary" size="md" />
          </>
        )}
        {...props}
      >
        <Flex
          direction="column"
          gap={3}
          css={css`
            padding: ${getSpacing('1')} 0;
          `}
        >
          <Flex gap={4}>
            <PatientConfirmationInfoCard
              icon="home"
              labelKey="recordingConsole:patientConfirmation.labels.where"
              mainValue={data?.locationName || unavailableText}
              bottomText={data?.serviceName}
              fixedWidth
            />
            <PatientConfirmationInfoCard
              icon="calendar"
              labelKey="recordingConsole:patientConfirmation.labels.when"
              mainValue={timeRange || unavailableText}
              secondaryLabelKey={showPlanSuffix ? 'recordingConsole:patientConfirmation.plan' : undefined}
              secondaryLabelValues={showPlanSuffix ? { start: plannedStartTime!, end: plannedEndTime! } : undefined}
              bottomText={surgeryDate || ''}
              highlighted={data?.hasDelay}
            />
          </Flex>

          <PatientConfirmationInfoCard
            icon="surgery"
            labelKey="recordingConsole:patientConfirmation.labels.procedure"
            mainValue={data?.procedureName || unavailableText}
            badgeKey="common:terms.surgeon"
            badgeValues={{ name: data?.surgeonName || unavailableText }}
            fullWidth
          />

          <PatientConfirmationInfoCard
            icon="user"
            labelKey="recordingConsole:patientConfirmation.labels.patientName"
            mainValue={data?.patientName || unavailableText}
            badgeKey="common:terms.mrn"
            badgeValues={{ mrn: data?.patientMrn || unavailableText }}
            fullWidth
          />
        </Flex>
      </CoreModal>
    );
  },
);

ConfirmPatientModal.displayName = 'ConfirmPatientModal';
